import { SelectedEvents, EventCount } from '../components/eventDashboard/loadDependencies';

export enum Filters {
    VALUE = 'value',
    SERVICE = 'service',
    EVENT_LEVEL = 'eventLevel'
}

type SimpleFilter = {
    value: string[];
    service: string[];
    eventLevel: string[];
}

type Key = string;

/**
 *
 * @param {SelectedEvents} p_filters
 * @returns {SimpleFilter[]}
 */
// eslint-disable-next-line complexity
const convertToSimpleFilter = (p_filters: SelectedEvents = {}): SimpleFilter => {
    const result = <SimpleFilter>({
        value: [],
        service: [],
        eventLevel: []
    });
    const {
        eventTypes: selectedEventTypes = [],
        eventServices: selectedEventServices = [],
        eventLevels: selectedEventLevels = []
    } = p_filters;
    for (const types of (selectedEventTypes as EventCount[])) {
        result.value.push('' + types.original?.value);
    }
    for (const services of (selectedEventServices as EventCount[])) {
        result.service.push('' + services.original?.service);
    }
    for (const levels of (selectedEventLevels as EventCount[])) {
        result.eventLevel.push('' + levels.original?.eventLevel);
    }
    return result;
};

// util functions for grouping with / without count
const groupByFieldNoCount = (data: EventCount[], key: Filters) => data.reduce((prev: Record<Key, number>, curr) => {
    prev[curr[key] as Key] = 0;
    return prev;
}, {} as Record<Key, number>);

const groupByFieldAndCount = (data: EventCount[], key: Filters) => data.reduce((prev: Record<Key, number>, curr) => {
    if (prev[curr[key] as Key]) {
        prev[curr[key] as Key] += curr.count;
    } else {
        prev[curr[key] as Key] = curr.count;
    }
    return prev;
}, {} as Record<Key, number>);

const applyFilters = (data: EventCount[], simpleFilter: SimpleFilter) => {
    // eslint-disable-next-line complexity
    return data.filter((data) => {
        return (simpleFilter.value.length === 0 || simpleFilter.value.some((filter) => filter === data.value)) &&
            (simpleFilter.service.length === 0 || simpleFilter.service.some((filter) => filter === data.service)) &&
            (simpleFilter.eventLevel.length === 0 || simpleFilter.eventLevel.some((filter) => filter === data.eventLevel));
    });
};

/**
 * Using the original list of Events (`p_eventTypes`) and the current filters (`p_filters`),
 * this function converts to a list of `EventCount` object that can be used for aggregation.
 * @param p_eventTypes: EventCount[]
 * @param p_filters: SelectedEvents
 * @returns: EventCount[]
*/
// eslint-disable-next-line complexity
export const generateFilteredEvents = (p_eventTypes: EventCount[]): EventCount[] => {
    const filteredEvents = <EventCount[]>([]);

    for (const eventType of p_eventTypes) {
        filteredEvents.push({
            name: `${eventType._id} (${eventType.count})`,
            value: eventType._id || '',
            count: eventType.count,
            service: eventType.service,
            eventLevel: eventType.eventLevel
        });
    }
    return filteredEvents;
};

/**
 * Using the original list of Events (`p_eventTypes`), this function create 3 filter list
 * @param p_eventTypes: EventCount[]
 * @returns: {  eventTypeFilters: SelectItemValue[];
 *              eventLevelFilters: SelectItemValue[];
 *              eventServiceFilters: SelectItemValue[];
 * }
 */
// eslint-disable-next-line complexity
export const generateFilters = (p_eventTypes: EventCount[], p_filters: SelectedEvents): {
    eventTypeFilters: EventCount[];
    eventLevelFilters: EventCount[];
    eventServiceFilters: EventCount[];
} => {
    // all keys, value is '0'
    const allValues = groupByFieldNoCount(p_eventTypes, Filters.VALUE);
    const allSvcs = groupByFieldNoCount(p_eventTypes, Filters.SERVICE);
    const allEvtLvls = groupByFieldNoCount(p_eventTypes, Filters.EVENT_LEVEL);

    // all keys, value is summed 'count' (DO WE NEED THIS FOR +1,+2 SCENARIOS?)
    const allValuesWCount = groupByFieldAndCount(p_eventTypes, Filters.VALUE);
    const allSvcsWCount = groupByFieldAndCount(p_eventTypes, Filters.SERVICE);
    const allEvtLvlsWCount = groupByFieldAndCount(p_eventTypes, Filters.EVENT_LEVEL);

    const simpleFilter = convertToSimpleFilter(p_filters);

    // filter data
    const filteredData = applyFilters(p_eventTypes, simpleFilter);

    // filtered keys, value is summed 'count'
    const filteredValues = groupByFieldAndCount(filteredData, Filters.VALUE);
    const filteredSvcs = groupByFieldAndCount(filteredData, Filters.SERVICE);
    const filteredEvtLvls = groupByFieldAndCount(filteredData, Filters.EVENT_LEVEL);

    // apply logic depending on how many filters are active.
    const count = (simpleFilter.value.length > 0 ? 1 : 0) +
        (simpleFilter.service.length > 0 ? 1 : 0) +
        (simpleFilter.eventLevel.length > 0 ? 1 : 0);

    let eventTypeFilters, eventServiceFilters, eventLevelFilters;
    if (count === 0) {
        // don't filter
        eventTypeFilters = allValuesWCount;
        eventServiceFilters = allSvcsWCount;
        eventLevelFilters = allEvtLvlsWCount;
    } else if (count === 1) {
        // special case, show numbers for the filtered category only
        if (simpleFilter.value.length > 0) {
            eventTypeFilters = allValuesWCount;
        } else {
            eventTypeFilters = { ...allValues, ...filteredValues };
        }
        if (simpleFilter.service.length > 0) {
            eventServiceFilters = allSvcsWCount;
        } else {
            eventServiceFilters = { ...allSvcs, ...filteredSvcs };
        }
        if (simpleFilter.eventLevel.length > 0) {
            eventLevelFilters = allEvtLvlsWCount;
        } else {
            eventLevelFilters = { ...allEvtLvls, ...filteredEvtLvls };
        }
    } else {
        // show multi-filters
        eventTypeFilters = { ...allValues, ...filteredValues };
        eventServiceFilters = { ...allSvcs, ...filteredSvcs };
        eventLevelFilters = { ...allEvtLvls, ...filteredEvtLvls };
    }

    eventTypeFilters = generateFilterItems(p_eventTypes, Filters.VALUE, eventTypeFilters)
        .sort((a, b) => a.name.localeCompare(b.name));
    eventServiceFilters = generateFilterItems(p_eventTypes, Filters.SERVICE, eventServiceFilters)
        .sort((a, b) => a.name.localeCompare(b.name));
    eventLevelFilters = generateFilterItems(p_eventTypes, Filters.EVENT_LEVEL, eventLevelFilters)
        .sort((a, b) => a.name.localeCompare(b.name));
    return {
        eventTypeFilters, eventServiceFilters, eventLevelFilters
    };
};

const generateFilterItems = (p_events: EventCount[], key: Filters, p_eventFilers: Record<string, number> = {}): EventCount[] => {
    const items = <{ [key: string]: EventCount }>({});
    for (const event of p_events) {
        items[event[key] as Key] = {
            ...event,
            name: `${event[key]} (${p_eventFilers[event[key] as Key] || 0})`
        };
    }
    return Object.values(items);
};